// Round execution, ported from SpinozaWeb: every profile runs concurrently on an identical
// history that excludes what the others produce in the same round (same-round isolation).
import { profileById } from '@/data/catalog';
import { profileDefinition } from '@/data/profiles';
import { currentLocale, t } from '@/i18n';
import { ADAPTERS, pickLightModel } from '@/llm';
import { parseChatResponse } from '@/llm/parse';
import { assembleHistory } from '@/llm/prompt/assemble';
import { buildSystemPrompt } from '@/llm/prompt/protocol';
import { describeError, LLMError, type ChatMessage, type LLMAdapter } from '@/llm/types';
import { activeLLM, settingsStore } from '@/settings/settingsStore';
import { appStore } from '@/state/appStore';
import type { Message, Thread } from '@/types';

const FLUSH_MS = 80;

export interface LLMConfig {
  adapter: LLMAdapter;
  apiKey: string;
  model: string;
  models: string[];
  modelsSource?: 'live' | 'fallback';
}

export function llmConfig(): LLMConfig {
  const settings = settingsStore.get();
  const p = activeLLM(settings);
  if (!p?.apiKey || !p.model) throw new LLMError('invalid_key', t('run.noLLM'));
  return { adapter: ADAPTERS[settings.llm.activeProvider], apiKey: p.apiKey, model: p.model, models: p.models ?? [], modelsSource: p.modelsSource };
}

export type RoundInput = { type: 'answer'; text: string } | { type: 'debate' };

export async function startRound(thread: Thread, input: RoundInput): Promise<void> {
  if (appStore.get().runs[thread.id]) throw new Error(t('run.busy'));
  const cfg = llmConfig();
  await appStore.ensureMessages(thread.id);
  const existing = appStore.get().messages[thread.id] ?? [];
  const round = existing.reduce((max, m) => Math.max(max, m.round), 0) + 1;
  let seq = existing.reduce((max, m) => Math.max(max, m.seq), -1) + 1;
  const untitled = thread.title === t('experts.untitled');

  if (input.type === 'answer') {
    await appStore.upsertMessage({ id: crypto.randomUUID(), threadId: thread.id, seq: seq++, round, roundType: 'answer', role: 'user', text: input.text, status: 'complete', createdAt: Date.now() });
  }

  const cards: Message[] = thread.profileIds.map((profileId) => ({
    id: crypto.randomUUID(), threadId: thread.id, seq: seq++, round, roundType: input.type, role: 'profile', profileId, status: 'streaming', raw: '', createdAt: Date.now(),
  }));
  for (const card of cards) await appStore.upsertMessage(card);

  const controller = new AbortController();
  appStore.setRun(thread.id, { threadId: thread.id, round, total: cards.length, done: 0, controller });
  await appStore.updateThread(thread.id, {});

  await Promise.allSettled(cards.map((card) => runCard(card, controller.signal, cfg)));
  appStore.setRun(thread.id, undefined);

  if (input.type === 'answer' && untitled) void generateTitle(thread.id, cfg);
}

export async function retryCard(thread: Thread, messageId: string): Promise<void> {
  if (appStore.get().runs[thread.id]) throw new Error(t('run.busy'));
  const cfg = llmConfig();
  const message = appStore.message(thread.id, messageId);
  if (!message || message.role !== 'profile') return;
  const reset: Message = { ...message, status: 'streaming', raw: '', error: undefined, opinions: undefined };
  await appStore.upsertMessage(reset);
  const controller = new AbortController();
  appStore.setRun(thread.id, { threadId: thread.id, round: message.round, total: 1, done: 0, controller });
  await runCard(reset, controller.signal, cfg);
  appStore.setRun(thread.id, undefined);
}

export function stopRound(threadId: string): void {
  appStore.get().runs[threadId]?.controller.abort();
}

async function runCard(card: Message, signal: AbortSignal, cfg: LLMConfig): Promise<void> {
  const latest = () => appStore.message(card.threadId, card.id) ?? card;
  const profile = profileById(card.profileId ?? '');
  try {
    if (!profile) throw new Error(t('run.unknownProfile', { id: card.profileId ?? '?' }));
    const { history, truncatedRounds } = await assembleHistory({
      messages: appStore.get().messages[card.threadId] ?? [],
      currentRound: card.round,
      roundType: card.roundType,
      selfProfileId: profile.id,
      loadImage: async () => null,
    });
    const system = buildSystemPrompt(profileDefinition(profile.id), { truncatedRounds, locale: currentLocale() });
    const stream = new DeltaBuffer(card);
    let raw = await cfg.adapter.chat({ system, messages: history, model: cfg.model, apiKey: cfg.apiKey, signal, jsonMode: true, onDelta: (d) => stream.push(d) });
    stream.stop();

    let opinions;
    try {
      opinions = parseChatResponse(raw);
    } catch {
      raw = await repairJson(cfg, system, history, raw, signal);
      opinions = parseChatResponse(raw);
    }
    await appStore.upsertMessage({ ...latest(), status: 'complete', opinions, raw, error: undefined });
  } catch (e) {
    await appStore.upsertMessage({ ...latest(), status: 'error', error: describeError(e) });
  } finally {
    appStore.bumpRunDone(card.threadId);
  }
}

function repairJson(cfg: LLMConfig, system: string, history: ChatMessage[], previous: string, signal: AbortSignal): Promise<string> {
  const messages: ChatMessage[] = [
    ...history,
    { role: 'assistant', content: [{ type: 'text', text: previous }] },
    { role: 'user', content: [{ type: 'text', text: t('run.repair') }] },
  ];
  return cfg.adapter.chat({ system, messages, model: cfg.model, apiKey: cfg.apiKey, signal, jsonMode: true });
}

class DeltaBuffer {
  private text = '';
  private timer: ReturnType<typeof setTimeout> | undefined;
  private stopped = false;
  constructor(private readonly card: Message) {}
  push(delta: string) {
    this.text += delta;
    if (this.stopped || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      if (this.stopped) return;
      const current = appStore.message(this.card.threadId, this.card.id) ?? this.card;
      void appStore.upsertMessage({ ...current, raw: this.text }, false);
    }, FLUSH_MS);
  }
  stop() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
  }
}

async function generateTitle(threadId: string, cfg: LLMConfig): Promise<void> {
  const messages = appStore.get().messages[threadId] ?? [];
  const question = messages.find((m) => m.role === 'user')?.text ?? '';
  if (!question) return;
  const conclusions = messages.filter((m) => m.role === 'profile' && m.status === 'complete').flatMap((m) => (m.opinions ?? []).slice(0, 2).map((o) => `- ${o.statement}`));
  const candidates = cfg.modelsSource === 'live' ? [pickLightModel(cfg.models, cfg.model), cfg.model] : [cfg.model];
  for (const model of new Set(candidates)) {
    try {
      const title = await cfg.adapter.chat({
        system: t('run.titleSystem'),
        messages: [{ role: 'user', content: [{ type: 'text', text: t('run.titleUser', { question: question.split('\n')[0], conclusions: conclusions.join('\n') }) }] }],
        model,
        apiKey: cfg.apiKey,
      });
      const clean = title.trim().split('\n')[0].replace(/^[\s「『"'“‘《*`#]+|[\s」』"'”’》。．.!！*`]+$/g, '').slice(0, 40);
      if (clean) {
        await appStore.updateThread(threadId, { title: clean });
        return;
      }
    } catch {
      // try the next candidate
    }
  }
}
